import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PublicMenuLayoutId, PublicMenuTheme } from "../../shared/public-menu-theme";
import { queryKeys } from "../../lib/queryKeys";
import { apiRequest, jsonBody } from "../lib/api";
import { useToast } from "../contexts/ToastContext";
import { Button, Checkbox as DashboardCheckbox, HeroSelectField } from "./Ui";

/** This component is mounted only in the protected super-admin business detail. */
export function AdminPublicMenuLayoutPanel({ businessId }: { businessId: string }) {
  const client = useQueryClient();
  const { show } = useToast();
  const [layoutId, setLayoutId] = useState<PublicMenuLayoutId>("default");
  const [applyBrandPreset, setApplyBrandPreset] = useState(false);
  const key = queryKeys.admin.businessLayout(businessId);
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiRequest<PublicMenuTheme>(`/api/admin/businesses/${businessId}/public-menu-layout`),
    enabled: Boolean(businessId),
  });
  useEffect(() => {
    if (query.data) { setLayoutId(query.data.layoutId); setApplyBrandPreset(false); }
  }, [query.data]);
  const mutation = useMutation({
    mutationFn: () => apiRequest<PublicMenuTheme>(`/api/admin/businesses/${businessId}/public-menu-layout`, {
      method: "PATCH", body: jsonBody({ layoutId, applyBrandPreset }),
    }),
    retry: 0,
    onSuccess: (theme) => {
      client.setQueryData(key, theme);
      show("Layout asignado. Los cambios se verán al actualizar el menú público.", "success");
    },
    onError: (error) => show(error instanceof Error ? error.message : "No se pudo asignar el layout.", "error"),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); mutation.mutate(); };
  return (
    <section className="grid gap-4 rounded-2xl border border-[var(--app-brand-line)] bg-[var(--app-brand-paper)]/70 p-4">
      <div><h3 className="font-black text-[var(--app-brand-ink)]">Diseño del menú público</h3><p className="text-xs text-[var(--app-brand-muted)]">La estructura la asignas aquí. El propietario conserva el control de sus colores y fuentes.</p></div>
      {query.isPending ? <p role="status">Cargando diseño...</p> : query.isError ? (
        <div role="alert"><p>No se pudo cargar la asignación.</p><Button variant="secondary" onPress={() => void query.refetch()}>Reintentar</Button></div>
      ) : (
        <form onSubmit={submit} className="grid gap-4">
          <HeroSelectField
            label="Layout asignado" value={layoutId} isDisabled={mutation.isPending}
            options={[{ id: "default", label: "Predeterminado" }, { id: "wingers", label: "WINGER'S - Diner conectado" }, { id: "dinnerking", label: "Dinner King - Clásico" }]}
            onChange={(value) => { if (value === "default" || value === "wingers" || value === "dinnerking") setLayoutId(value); }}
          />
          <DashboardCheckbox
            isSelected={applyBrandPreset} isDisabled={mutation.isPending} onChange={setApplyBrandPreset}
            label={layoutId === "wingers" ? "Aplicar también la paleta y fuentes de WINGER'S" : "Restaurar también la paleta predeterminada"}
            description="Opcional. Reemplaza colores y tipografías actuales; no cambia logo, productos ni precios."
          />
          <div className="flex justify-end"><Button type="submit" busy={mutation.isPending}>Guardar diseño</Button></div>
        </form>
      )}
    </section>
  );
}
